(ns ncr.widgets.grid
  (:require
   [lambdaisland.glogi :as log]
   [uix.core.alpha :as uix]
   [ncr.widgets.core :as w]
   [ncr.widgets.util :as wu]
   [ncr.etc.util :as u]
   [ncr.widgets.attributes :as wa]
   ["@tanstack/react-table" :as rt]))

(defn value [^js in]
  (.getValue in))

(defn row [^js in]
  (.-row in))

(defn table-meta [^js cell-context]
  (-> cell-context .-table .-options .-meta))

(defn row-id [^js cell-context]
  (-> cell-context .-row .-id))

(defn- tcell [tag ^js cell-context body]
  (let [key (-> cell-context .-column .-id)
        v ((-> cell-context table-meta :getExtendedDataRaw))]
    (into [tag]
      (wu/collect-props {:key key :id key :class (when (-> v vals first first (= key)) :ncr-m-extended)}
        body cell-context))))

(defn column-accessor
  ([column cell-fn]
   (column-accessor column (constantly [:span "#"]) cell-fn))
  ([column header cell-fn]
   (.accessor (rt/createColumnHelper) identity
     #js
      {:id column
       :header
       (fn [header-context]
         (tcell :th header-context
           header))
       :cell
       (fn [cell-context]
         (tcell :td cell-context
           (cell-fn cell-context)))})))

(defn- flex-render [comp context]
  (if (vector? comp)
    comp (comp context)))

(defn collapse-all! [^js table]
  (when table
    ((-> table .-options .-meta :collapseAll))))

(defn- generate-ids [rows prefix]
  (map-indexed
    (fn [ix {:keys [sub-rows] :as row}]
      (let
        [id (str prefix ix)
         row (merge {:id id} row)]
        (if (seq sub-rows)
          (update row :sub-rows generate-ids (str id "-"))
          row)))
    rows))

(def extension-error-boundary
  (w/error-boundary
    (fn [error]
      [w/c-inline-error
       {:title "Extension error"
        :message (.-message error)}])))

(defn- filter-expand
  ([node-fn rows]
   (filter-expand 0 node-fn rows nil))
  ([level node-fn rows parents]
   (->> rows
     (mapcat
       (fn [{:keys [id sub-rows] :as node}]
         (when (seq sub-rows)
           (let [parents (conj parents [[level id] true])]
             (concat
               (when (node-fn node) parents)
               (filter-expand (inc level) node-fn sub-rows parents))))))
     (into {}))))

(defn- row-key [[depth path]]
  (str "row_" depth "_" (u/path->key path)))

(defn- c-tbody-row [{:keys [^js row col-span extension]}]
  [:<>
   [:tr {:class [(some->> (.-original row) :type name (str "ncr-m-"))
                 (when extension :ncr-m-extended)]}
    (map
      (fn [^js cell]
        (flex-render (-> cell .-column .-columnDef .-cell) (.getContext cell)))
      (.getVisibleCells row))]

   (when extension
     [:tr {:class :ncr-m-extended}
      [:td {:class :pf-u-p-0 :col-span col-span}
       [extension-error-boundary extension]]])])

(defn grid [{:keys [skip-header table-ref initial-expand] :or {table-ref (uix/ref nil)} :as props} {:keys [rows columns meta]}]
  (let
    [rows
     (uix/memo #(-> rows (generate-ids "")) [rows])

     expanded
     (uix/state
       (clj->js
         (cond
           (fn? initial-expand) (filter-expand initial-expand rows)
           (= initial-expand ::all) true
           :else nil)))

     extended-rows
     (uix/state {})
     -extended-rows @extended-rows

     exclusive-row-extension? true

     meta
     (uix/memo
       (fn []
         (merge
           {:getExtendedData
            (fn [^js cell-context]
              (let [col-id (-> cell-context .-column .-id)
                    [col-id' ext-data] (get @extended-rows (row-id cell-context))]
                (when (= col-id col-id') ext-data)))

            :getExtendedDataRaw #(deref extended-rows)

            :toggleRowExtended
            (fn
              ([^js cell-context extra]
               (let
                 [col-id (-> cell-context .-column .-id)
                  row-id (row-id cell-context)
                  cell-data [col-id extra]]

                 (if-not exclusive-row-extension?
                   (swap! extended-rows assoc row-id cell-data)
                   (reset! extended-rows {row-id cell-data}))))
              ([cell-context]
               (if-not exclusive-row-extension?
                 (swap! extended-rows dissoc (row-id cell-context))
                 (reset! extended-rows {}))))

            :collapseAll
            (fn [& _]
              (.toggleAllRowsExpanded ^js @table-ref false)
              (reset! extended-rows {}))}
           meta))
       [exclusive-row-extension? meta])

     ^js table
     (rt/useReactTable
       #js
        {:onExpandedChange
         (fn [ex]
           (swap! expanded ex))
         :state
         #js {:expanded @expanded}

         :enableExpanding (:enable-expanding props true)
         :data (to-array rows)
         :columns columns
         :meta meta
         :getCoreRowModel (rt/getCoreRowModel)
         :getRowId
         (let [ids (atom #{})]
           (fn [{:keys [id]} _ parent]
             (let [id [(if parent (inc (.-depth parent)) 0) id]]
               (if (@ids id)
                 (log/error :what "row id uniqueness violation" :id id)
                 (swap! ids conj id))
               id)))

         :getSubRows
         (fn [row]
           (apply array
             (get row :sub-rows)))

         :getExpandedRowModel
         (rt/getExpandedRowModel)})

     extended-col (some-> -extended-rows vals first first)]

    (reset! table-ref table)

    [:table
     (w/merge-props
       {:role :grid
        :class (when extended-col :ncr-m-extended)}
       (wa/filter props))
     (when-not skip-header
       (into [:thead]
         (map
           (fn [hg]
             (into [:tr]
               (map
                 (fn [^js h]
                   (when-not (.-isPlaceholder h)
                     (flex-render (-> h .-column .-columnDef .-header) (.getContext h))))
                 (.-headers hg))))
           (.getHeaderGroups table))))

     [:tbody
      (for [^js row (.-rows (.getRowModel table))
            :let [row-id (.-id row)
                  [col-id extra] (get -extended-rows row-id)]]
        [c-tbody-row
         {:key (row-key row-id)
          :row row
          :extension (when-let [extension (some->> col-id (.getValue row) :extension)]
                       [extension meta extra])
          :col-span (count (.getAllCells row))}])]]))

(defn tools [& tools]
  (into [:span.ncr-grid-tools]
    (for [[icon on-click] tools]
      [:span.ncr-grid-tool {:on-click on-click}
       [w/c-mdi-icon {:icon icon}]])))
